// src/pages/OltDetalle.jsx — una OLT por dentro
//
// Dos mitades que vienen de sitios distintos, y conviene tenerlo presente
// al tocar este archivo:
//
//   EL CHASIS se le pregunta a la OLT cada vez y se tira. La página del
//   switch funciona así entera. Aquí no alcanza, porque LA OLT NO SABE por
//   qué fibra sale cada PON: no hay comando que lo responda. Ese dato solo
//   existe en el rack, y si la app no lo guarda no lo guarda nadie.
//
// De ahí que los bastidores vayan justo debajo del chasis: la pregunta que
// se contesta aquí es "qué PON llega a esta posición", y se contesta
// mirando las dos cosas juntas.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Switches.css"; // las tarjetas, la tabla y el modal son los mismos
import "./Olt.css";
import { authFetch } from "../utils/authFetch";
import { Boton, EncabezadoPagina, Iconos, Tarjeta } from "../componentes/base";
import OltEquipo from "./OltEquipo";
import OltPerfiles from "./OltPerfiles";
import NubeParticulas from "../componentes/espera/NubeParticulas";

/* ------------------------------------------------------------------ */
/* Las placas, por color de fibra                                     */
/* ------------------------------------------------------------------ */

/**
 * El orden de colores de la norma TIA-598-C, que es el mismo con el que
 * vienen rotulados los tubos y las placas. Se usa el color y no una letra
 * cualquiera porque es lo que se ve en el bastidor: nadie busca "la placa
 * B", busca la naranja.
 */
const COLORES_FIBRA = [
  { clave: "azul", nombre: "Azul" },
  { clave: "naranja", nombre: "Naranja" },
  { clave: "verde", nombre: "Verde" },
  { clave: "cafe", nombre: "Café" },
  { clave: "gris", nombre: "Gris" },
  { clave: "blanco", nombre: "Blanco" },
  { clave: "rojo", nombre: "Rojo" },
  { clave: "negro", nombre: "Negro" },
  { clave: "amarillo", nombre: "Amarillo" },
  { clave: "violeta", nombre: "Violeta" },
  { clave: "rosa", nombre: "Rosa" },
  { clave: "aqua", nombre: "Aqua" },
];

const SECCIONES = [
  { clave: "panel", titulo: "Panel: chasis y bastidores", icono: Iconos.panel },
  {
    clave: "equipo",
    titulo: "El equipo: tarjetas, subida, VLANs y gestión",
    icono: Iconos.ajustes,
  },
  {
    clave: "perfiles",
    titulo: "Configuración para dar de alta: planes, perfiles, modelos y TR-069",
    icono: Iconos.planes,
  },
];

const letraModulo = (n) => String.fromCharCode(64 + n);
const colorModulo = (n) => COLORES_FIBRA[(n - 1) % COLORES_FIBRA.length];

/* ------------------------------------------------------------------ */
/* La espera                                                          */
/* ------------------------------------------------------------------ */

/**
 * Lo que esta pantalla le pregunta al equipo, en el orden en que sale por
 * la sesión SSH. Son los `display` de verdad: nada de adorno girando.
 */
const LECTURAS_PANEL = [
  { icono: Iconos.info, rotulo: "Versión y parche" },
  { icono: Iconos.panel, rotulo: "Chasis y tarjetas" },
  { icono: Iconos.senal, rotulo: "Puertos PON" },
  { icono: Iconos.onus, rotulo: "ONTs de cada tarjeta" },
  { icono: Iconos.equipos, rotulo: "VLANs" },
  { icono: Iconos.ajustes, rotulo: "Subida y gestión" },
];

/**
 * La misma nube de la pestaña de configuración y del switch, con las
 * lecturas de esta pantalla: así las esperas se ven igual y se entiende
 * que es el mismo trabajo, no pantallas distintas.
 *
 * El reloj está porque es lo único que se puede afirmar con certeza: la
 * OLT no informa de su avance, así que un contador es toda la diferencia
 * entre "está trabajando" y "se colgó".
 */
function CargandoOlt({ nombre, host }) {
  return (
    <Tarjeta>
      <NubeParticulas
        titulo={`Leyendo ${nombre}`}
        lecturas={LECTURAS_PANEL}
        nota={`Una sola sesión SSH, y en ella el chasis, las ONTs de cada tarjeta y la configuración. ${host}`.trim()}
        umbral={40}
        aviso="lo normal son unos quince, y si pasa del minuto y medio algo se atoró"
      />
    </Tarjeta>
  );
}

/* ------------------------------------------------------------------ */
/* El chasis                                                          */
/* ------------------------------------------------------------------ */

/**
 * El estado del PON, en tres cajones y no más: libre, arriba o caído.
 *
 * Se deduce de las ONTs porque es lo que de verdad dice si ese puerto
 * está sirviendo: un PON con 45 ONTs y NINGUNA en línea es un puerto
 * caído —fibra cortada o puerto apagado—, mientras que uno con 45 y 43 en
 * línea está perfectamente arriba y los dos que faltan son clientes con la
 * ONT desconectada, que no es problema del PON.
 *
 * Si el firmware no reporta `online:` no se puede distinguir arriba de
 * caído, y entonces tener ONTs cuenta como arriba: mejor eso que pintar
 * de rojo algo que nadie midió.
 */
function estadoPon(p) {
  // El módulo óptico del propio puerto manda sobre cualquier cuenta de
  // ONTs: si está caído es una avería de hardware, y entonces da igual
  // cuántos clientes tuviera colgados. Además llega en la primera
  // lectura, la barata, así que una avería se ve enseguida.
  if (p.optico && p.optico !== "online") return "caido";
  // Las cuentas vienen en una segunda lectura. Mientras no lleguen no se
  // dice si está libre: pintar 64 puertos de gris y corregirlos tres
  // segundos después es peor que decir que todavía no se sabe.
  if (p.onts === null || p.onts === undefined) return "contando";
  if (!p.onts) return "libre";
  if (p.online !== null && p.online === 0) return "caido";
  return "arriba";
}

function clasePon(p, hayMapa) {
  let clase = `olt-pon olt-pon--${estadoPon(p)}`;
  // El punteado solo aparece cuando YA hay mapa. Con la tabla vacía,
  // marcar los 64 puertos como "sin fibra" sería puro ruido.
  if (hayMapa && !p.odf) clase += " olt-pon--sinfibra";
  return clase;
}

const TEXTO_ESTADO = { libre: "libre", arriba: "arriba", caido: "CAÍDO", contando: "contando ONTs…" };

function tituloPon(p) {
  const partes = [p.fsp, TEXTO_ESTADO[estadoPon(p)]];
  if (p.optico && p.optico !== "online") partes.push(`módulo óptico ${p.optico}`);
  if (p.onts) {
    partes.push(
      p.online !== null
        ? `${p.online} de ${p.onts} ONTs en línea`
        : `${p.onts} ONTs`,
    );
  }
  partes.push(
    p.odf
      ? `${p.odf.nombre} · placa ${letraModulo(p.odf.modulo)} · posición ${p.odf.numero}` +
          (p.odf.etiqueta ? ` · ${p.odf.etiqueta}` : "")
      : "sin fibra registrada",
  );
  return partes.join(" · ");
}

/**
 * Las ranuras en vertical, que es como se lee un chasis: la numeración va
 * de arriba abajo y una tarjeta ocupa un renglón. Distinto del switch, que
 * sí es una hilera de conectores.
 */
function Chasis({ ranuras, hayMapa }) {
  // Solo las GPON. Las de control y las de subida son hardware y viven en
  // la otra pantalla: aquí lo que se mira es el servicio, y sus cuatro
  // puertos de 10G no dicen nada de eso.
  const gpon = ranuras.filter((r) => r.tipo === "GPON");

  // `display board 0` solo reporta las ranuras OCUPADAS. Las vacías se
  // rellenan hasta la última GPON para que el chasis se vea completo:
  // saber que la 4 y la 6 están libres es lo que se mira antes de comprar
  // una tarjeta.
  const ultima = gpon.length ? Math.max(...gpon.map((r) => r.slot)) : -1;
  const porSlot = new Map(gpon.map((r) => [r.slot, r]));
  const todas = Array.from(
    { length: ultima + 1 },
    (_, i) => porSlot.get(i) || { slot: i, vacia: true },
  );

  return (
    <div className="olt-chasis">
      {todas.map((r) => (
        <div
          key={r.slot}
          className={`olt-ranura${r.vacia ? " olt-ranura--vacia" : ""}`}
        >
          <span className="olt-ranura__id">0/{r.slot}</span>

          <div className="olt-puertos">
            {r.vacia ? (
              <span className="olt-vacio">ranura libre</span>
            ) : (
              r.puertos.map((p) => (
                <span
                  key={p.pon}
                  className={clasePon(p, hayMapa)}
                  title={tituloPon(p)}
                >
                  {p.pon}
                </span>
              ))
            )}
          </div>

          <div className="olt-ranura__tarjeta">
            {r.vacia ? (
              "—"
            ) : (
              <>
                <b>{r.tarjeta}</b>
                {`GPON · ${r.puertos.length} puertos`}
                {/* `Auto_find` = la OLT la ve pero no está dada de alta, y
                    hasta que se registre no admite service-ports. */}
                {!r.registrada && (
                  <em className="olt-sinalta">sin dar de alta</em>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Los bastidores                                                     */
/* ------------------------------------------------------------------ */

/**
 * Un LP-ODF-8024 son cuatro placas y cada placa, una LP-FO-S12, lleva sus
 * 12 acopladores SC en DOS FILAS DE SEIS. Se dibuja así porque quien lo
 * mira lo está comparando con el bastidor de la pared, y ahí no hay una
 * hilera de doce.
 */
function Bastidor({ odf, onPosicion }) {
  const porFila = Math.ceil(odf.porModulo / (odf.filas || 1));

  return (
    <Tarjeta>
      <div className="olt-odf__cabecera">
        <div>
          <h3 className="olt-odf__nombre">{odf.nombre}</h3>
          <p className="olt-odf__modelo">
            {[
              odf.modelo,
              `${odf.total} fibras`,
              `${odf.modulos.length} placas de ${odf.porModulo}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <span className="olt-odf__cuenta">
          {odf.ocupadas} de {odf.total}
        </span>
      </div>

      <div className="olt-odf__placas">
        {odf.modulos.map((m) => {
          const color = colorModulo(m.modulo);
          return (
            <div key={m.modulo} className="olt-placa">
              <span
                className={`olt-placa__id olt-placa__id--${color.clave}`}
                title={`Placa ${letraModulo(m.modulo)} · ${color.nombre}`}
              >
                {letraModulo(m.modulo)}
              </span>
              <div
                className="olt-placa__fibras"
                style={{ gridTemplateColumns: `repeat(${porFila}, 1fr)` }}
              >
                {m.posiciones.map((pos) => (
                  <button
                    key={pos.numero}
                    type="button"
                    className={`olt-fibra${pos.pon ? " olt-fibra--puesta" : ""}`}
                    onClick={() => onPosicion(odf, pos)}
                    title={
                      `${odf.nombre} · placa ${letraModulo(m.modulo)} (${color.nombre}) · posición ${pos.numero}` +
                      (pos.pon ? ` · PON ${pos.pon}` : " · libre") +
                      (pos.etiqueta ? ` · ${pos.etiqueta}` : "")
                    }
                  >
                    <b>{pos.numero}</b>
                    <span>{pos.pon ? pos.pon.replace(/^0\//, "") : "—"}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Tarjeta>
  );
}

/* ------------------------------------------------------------------ */
/* Conectar una fibra                                                 */
/* ------------------------------------------------------------------ */

function ModalFibra({ destino, pons, guardando, error, onGuardar, onCerrar }) {
  const { odf, pos } = destino;
  const color = colorModulo(pos.modulo);

  const [pon, setPon] = useState(pos.pon || "");
  const [etiqueta, setEtiqueta] = useState(pos.etiqueta || "");

  // Agrupadas por tarjeta, que es como se buscan: primero la ranura y
  // después el puerto.
  const porTarjeta = useMemo(() => {
    const mapa = new Map();
    pons.forEach((p) => {
      const clave = p.fsp.split("/").slice(0, 2).join("/");
      if (!mapa.has(clave)) mapa.set(clave, []);
      mapa.get(clave).push(p);
    });
    return [...mapa.entries()];
  }, [pons]);

  return (
    <div
      className="sw-modal"
      role="dialog"
      onClick={() => !guardando && onCerrar()}
    >
      <div
        className="sw-modal__caja sw-modal__caja--angosta"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sw-modal__cabecera">
          <h3>
            {odf.nombre} · placa{" "}
            <em className={`olt-placa__id olt-placa__id--${color.clave}`}>
              {letraModulo(pos.modulo)}
            </em>{" "}
            · posición {pos.numero}
          </h3>
          <button
            type="button"
            className="sw-icono"
            onClick={onCerrar}
            title="Cerrar"
          >
            <Iconos.cerrar size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="sw-modal__cuerpo">
          <label className="olt-campo">
            <span>Puerto PON que llega aquí</span>
            <select
              className="sw-modo"
              value={pon}
              onChange={(e) => setPon(e.target.value)}
              disabled={guardando}
            >
              <option value="">Sin conectar</option>
              {porTarjeta.map(([tarjeta, lista]) => (
                <optgroup key={tarjeta} label={`Tarjeta ${tarjeta}`}>
                  {lista.map((p) => {
                    // Un PON va en una sola posición. Los que ya están en
                    // otra salen deshabilitados y DICIENDO DÓNDE, que es
                    // más útil que esconderlos y dejarte buscando.
                    const ocupado =
                      p.odf &&
                      !(
                        p.odf.odfId === odf.id &&
                        p.odf.modulo === pos.modulo &&
                        p.odf.numero === pos.numero
                      );
                    return (
                      <option key={p.fsp} value={p.fsp} disabled={ocupado}>
                        {p.fsp}
                        {p.onts ? ` · ${p.onts} ONTs` : " · sin ONTs"}
                        {ocupado
                          ? ` · ya en ${p.odf.nombre} ${letraModulo(p.odf.modulo)}${p.odf.numero}`
                          : ""}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          </label>

          <label className="olt-campo">
            <span>Etiqueta</span>
            <input
              className="sw-nombre olt-entrada"
              value={etiqueta}
              onChange={(e) => setEtiqueta(e.target.value)}
              placeholder="Lo que dice la cinta: Ramal centro, Splitter 1:8…"
              maxLength={120}
              disabled={guardando}
            />
          </label>

          {error && (
            <p className="sw-aviso sw-aviso--bloqueo">
              <Iconos.error size={16} strokeWidth={2} />
              {error}
            </p>
          )}
        </div>

        <div className="sw-modal__pie">
          <Boton variante="secundario" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Boton>
          {(pos.pon || pos.etiqueta) && (
            <Boton
              variante="peligro"
              icono={Iconos.eliminar}
              onClick={() => onGuardar(null, "")}
              disabled={guardando}
            >
              Liberar
            </Boton>
          )}
          <Boton
            icono={Iconos.confirmar}
            onClick={() => onGuardar(pon || null, etiqueta)}
            cargando={guardando}
          >
            Guardar
          </Boton>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pantalla                                                           */
/* ------------------------------------------------------------------ */

export default function OltDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [contandoOnts, setContandoOnts] = useState(false);
  const [seccion, setSeccion] = useState("panel");
  const [destino, setDestino] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [errorFibra, setErrorFibra] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      // Las dos pantallas de una sola vez: una petición, una sesión SSH.
      // El login a la OLT cuesta más que varios `display`, así que
      // pedirlas por separado pagaba ese peaje dos veces.
      const res = await authFetch(`/api/olt/${id}/todo`);
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "No se pudo leer la OLT.");
      setDatos(data);
      return data;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setCargando(false);
    }
  }, [id]);

  /**
   * Las cuentas de ONTs, en una segunda vuelta.
   *
   * Es la lectura cara —la OLT recorre las ONTs de cada tarjeta entera— y
   * sacarla del camino crítico es lo que hace que la página aparezca
   * enseguida en vez de esperar a que termine todo.
   */
  const cargarOnts = useCallback(async (slots) => {
    if (!slots?.length) return;
    setContandoOnts(true);
    try {
      const res = await authFetch(`/api/olt/${id}/onts?slots=${slots.join(",")}`);
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "No se pudieron contar las ONTs.");
      setDatos((antes) => (antes ? conOnts(antes, data) : antes));
    } catch (e) {
      // No es un fallo de la pantalla: el chasis ya está puesto y sirve.
      // Se dice, y los PON se quedan diciendo que no se pudo contar.
      setError(e.message);
    } finally {
      setContandoOnts(false);
    }
  }, [id]);


  // Primero lo barato, y en cuanto está pintado, lo caro.
  useEffect(() => {
    cargar().then((data) => cargarOnts(data?.panel?.resumen?.slotsGpon));
  }, [cargar, cargarOnts]);

  /**
   * Guardar NO recarga el panel. Volver a leer costaría otra sesión SSH
   * de varios segundos, y esto se usa cargando el mapa posición por
   * posición: serían cincuenta esperas seguidas. Se actualiza el estado
   * local con lo que acaba de aceptar el servidor.
   */
  const guardar = async (pon, etiqueta) => {
    setGuardando(true);
    setErrorFibra(null);
    const { odf, pos } = destino;
    try {
      const res = await authFetch(
        `/api/olt/${id}/odf/${odf.id}/${pos.modulo}/${pos.numero}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pon, etiqueta }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.ok)
        throw new Error(data.error || "No se pudo guardar.");

      setDatos((antes) => ({
        ...antes,
        panel: aplicarFibra(antes.panel, odf.id, pos, pon, etiqueta || null),
      }));
      setDestino(null);
    } catch (e) {
      setErrorFibra(e.message);
    } finally {
      setGuardando(false);
    }
  };

  if (cargando && !datos) {
    return (
      <div className="sw-pagina">
        <EncabezadoPagina icono={Iconos.olt} titulo="OLT" />
        <CargandoOlt nombre={`la OLT ${id}`} host="" />
      </div>
    );
  }

  if (error && !datos) {
    return (
      <div className="sw-pagina">
        <EncabezadoPagina icono={Iconos.olt} titulo="OLT" />
        <p className="sw-aviso sw-aviso--bloqueo">
          <Iconos.error size={16} strokeWidth={2} />
          {error}
        </p>
        <div className="sw-acciones">
          <Boton icono={Iconos.actualizar} onClick={cargar}>
            Reintentar
          </Boton>
        </div>
      </div>
    );
  }

  const { olt, ranuras, odfs, resumen } = datos.panel;
  const gpon = ranuras.filter((r) => r.tipo === "GPON");
  const pons = gpon.flatMap((r) => r.puertos);
  const enUso = resumen.ponsTotal
    ? (resumen.ponsConOnts / resumen.ponsTotal) * 100
    : 0;
  const conFibra = pons.filter((p) => p.odf).length;

  return (
    <div className="sw-pagina">
      <EncabezadoPagina
        icono={Iconos.olt}
        titulo={olt.nombre}
        resumen={[olt.host, olt.version, olt.patch].filter(Boolean).join(" · ")}
        acciones={
          <>
            <Boton
              variante="secundario"
              icono={Iconos.actualizar}
              onClick={cargar}
              cargando={cargando}
            >
              Actualizar
            </Boton>
            <Boton
              variante="secundario"
              icono={Iconos.atras}
              onClick={() => navigate("/olt-config")}
            >
              OLTs
            </Boton>
          </>
        }
      />

      {error && (
        <p className="sw-aviso sw-aviso--bloqueo">
          <Iconos.error size={16} strokeWidth={2} />
          {error}
        </p>
      )}

      <div className="sw-detalle">
        <nav className="sw-rail" aria-label="Secciones de la OLT">
          {SECCIONES.map((sec) => (
            <button
              key={sec.clave}
              type="button"
              className={`sw-rail__boton${seccion === sec.clave ? " sw-rail__boton--activo" : ""}`}
              onClick={() => setSeccion(sec.clave)}
              title={sec.titulo}
              aria-label={sec.titulo}
            >
              <sec.icono size={20} strokeWidth={1.8} />
            </button>
          ))}
        </nav>

        <div className="sw-detalle__contenido">
          {seccion === "perfiles" && <OltPerfiles oltId={id} />}

          {seccion === "equipo" && (
            <OltEquipo
              oltId={id}
              host={olt.host}
              datos={datos.equipo}
              recargar={cargar}
              recargando={cargando}
            />
          )}

          {seccion === "panel" && (
            <>
              {datos.tiempos && (
        <details className="olt-tiempos">
          <summary>
            Leído en {(datos.tiempos.total / 1000).toFixed(1)} s
            {contandoOnts && " · contando ONTs…"}
          </summary>
          <table>
            <tbody>
              {datos.tiempos.comandos.map((c) => (
                <tr key={c.cmd}>
                  <td>{c.ms} ms</td>
                  <td><code>{c.cmd}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <div className="olt-encabezado">
                <Tarjeta>
                  <div className="olt-ficha">
                    <div>
                      <h2 className="sw-equipo__nombre">{olt.nombre}</h2>
                      <p className="sw-equipo__uptime">{olt.uptime || "—"}</p>
                    </div>
                    <div className="olt-cifra">
                      <b>{resumen.ponsConOnts}</b>
                      <span>de {resumen.ponsTotal} PON en uso</span>
                      <div className="olt-barra">
                        <i style={{ width: `${Math.max(2, enUso)}%` }} />
                      </div>
                    </div>
                  </div>

                  <div className="sw-equipo__datos">
                    <div className="sw-dato">
                      <span>Dirección IP</span>
                      <strong>{olt.host}</strong>
                    </div>
                    <div className="sw-dato">
                      <span>Versión</span>
                      <strong>{olt.version || "—"}</strong>
                    </div>
                    <div className="sw-dato">
                      <span>Parche</span>
                      <strong>{olt.patch || "—"}</strong>
                    </div>
                    <div className="sw-dato">
                      <span>Tarjetas GPON</span>
                      <strong>{gpon.length}</strong>
                    </div>
                  </div>
                </Tarjeta>

                <Tarjeta titulo="ONTs">
                  {resumen.ontsPendientes ? (
                    <div className="olt-contando" role="status" aria-live="polite">
                      <Iconos.cargando size={16} strokeWidth={2.2} />
                      <div>
                        <b>Contando las ONTs de las {gpon.length} tarjetas</b>
                        <span>
                          Es la lectura cara: la OLT recorre las ONTs de cada
                          tarjeta entera. El chasis de abajo ya es real.
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="olt-totales">
                      <div className="olt-total">
                        <b>{(resumen.ontsTotal || 0).toLocaleString("es-MX")}</b>
                        <span>registradas</span>
                      </div>
                      {/* Solo si el firmware reportó `online:`. Un cero ahí
                          se leería como caída general. */}
                      {resumen.ontsEnLinea !== null && (
                        <>
                          <div className="olt-total">
                            <b className="olt-total--ok">
                              {resumen.ontsEnLinea.toLocaleString("es-MX")}
                            </b>
                            <span>en línea</span>
                          </div>
                          <div className="olt-total">
                            <b className={resumen.ontsTotal - resumen.ontsEnLinea ? "olt-total--mal" : undefined}>
                              {(resumen.ontsTotal - resumen.ontsEnLinea).toLocaleString("es-MX")}
                            </b>
                            <span>fuera de línea</span>
                          </div>
                        </>
                      )}
                      <div className="olt-total">
                        <b>{conFibra}</b>
                        <span>PON con fibra ubicada</span>
                      </div>
                    </div>
                  )}
                </Tarjeta>
              </div>

              <Tarjeta titulo="Chasis">
                <Chasis ranuras={ranuras} hayMapa={resumen.hayMapa} />
                <div className="sw-leyenda">
                  <span>
                    <i className="olt-punto olt-pon--arriba" />
                    arriba
                  </span>
                  <span>
                    <i className="olt-punto olt-pon--caido" />
                    caído
                  </span>
                  <span>
                    <i className="olt-punto olt-pon--libre" />
                    libre
                  </span>
                  {resumen.ontsPendientes && (
                    <span><i className="olt-punto olt-pon--contando" />contando…</span>
                  )}
                  {resumen.hayMapa && (
                    <span>
                      <i className="olt-punto olt-pon--sinfibra" />
                      sin fibra registrada
                    </span>
                  )}
                </div>
              </Tarjeta>

              {odfs.length === 0 ? (
                <Tarjeta>
                  <p className="sw-tenue">
                    Esta OLT no tiene bastidores dados de alta. Se crean con la
                    migración <code>012_olt_odf.sql</code>.
                  </p>
                </Tarjeta>
              ) : (
                odfs.map((odf) => (
                  <Bastidor
                    key={odf.id}
                    odf={odf}
                    onPosicion={(o, pos) => {
                      setErrorFibra(null);
                      setDestino({ odf: o, pos });
                    }}
                  />
                ))
              )}
            </>
          )}
        </div>
      </div>

      {destino && (
        <ModalFibra
          destino={destino}
          pons={pons}
          guardando={guardando}
          error={errorFibra}
          onGuardar={guardar}
          onCerrar={() => {
            setDestino(null);
            setErrorFibra(null);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Deja el panel como quedó después de conectar o liberar una fibra, sin
 * volver a preguntarle a la OLT. Toca los dos lados —la posición del
 * bastidor y el PON del chasis— porque el cruce se enseña en ambos.
 */
/**
 * Mete las cuentas de ONTs en el panel ya pintado. Toca los puertos y el
 * resumen; el chasis y el ODF se quedan como estaban.
 */
function conOnts(datos, onts) {
  const panel = datos.panel;
  return {
    ...datos,
    panel: {
      ...panel,
      ranuras: panel.ranuras.map((r) => ({
        ...r,
        puertos: r.puertos.map((p) => {
          const d = onts.porPuerto[p.fsp];
          return d ? { ...p, onts: d.onts, online: d.online } : { ...p, onts: 0 };
        }),
      })),
      resumen: {
        ...panel.resumen,
        ontsPendientes: false,
        ontsTotal: onts.ontsTotal,
        ontsEnLinea: onts.ontsEnLinea,
        ponsConOnts: onts.ponsConOnts,
      },
    },
  };
}

function aplicarFibra(datos, odfId, pos, pon, etiqueta) {
  const bastidor = datos.odfs.find((o) => o.id === odfId);
  const antes = bastidor?.modulos
    .find((m) => m.modulo === pos.modulo)
    ?.posiciones.find((p) => p.numero === pos.numero);
  const ponAnterior = antes?.pon || null;

  const odfs = datos.odfs.map((o) => {
    if (o.id !== odfId) return o;
    let ocupadas = o.ocupadas;
    if (!antes?.pon && !antes?.etiqueta && (pon || etiqueta)) ocupadas += 1;
    if ((antes?.pon || antes?.etiqueta) && !pon && !etiqueta) ocupadas -= 1;
    return {
      ...o,
      ocupadas,
      modulos: o.modulos.map((m) =>
        m.modulo !== pos.modulo
          ? m
          : {
              ...m,
              posiciones: m.posiciones.map((p) =>
                p.numero !== pos.numero ? p : { ...p, pon, etiqueta },
              ),
            },
      ),
    };
  });

  const enlace = pon
    ? {
        odfId,
        nombre: bastidor?.nombre,
        modulo: pos.modulo,
        numero: pos.numero,
        etiqueta,
      }
    : null;

  const ranuras = datos.ranuras.map((r) => ({
    ...r,
    puertos: r.puertos.map((p) => {
      if (p.fsp === pon) return { ...p, odf: enlace };
      // El PON que estaba aquí antes y ya no: se queda sin fibra.
      if (p.fsp === ponAnterior) return { ...p, odf: null };
      return p;
    }),
  }));

  const hayMapa = odfs.some((o) => o.ocupadas > 0);
  return { ...datos, odfs, ranuras, resumen: { ...datos.resumen, hayMapa } };
}
